/**
 * @description Component-TradePositionMobile
 */
import './index.less';

import classNames from 'classnames';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import Empty from '@/components/Empty';
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import PositionSizeDisplay from '@/components/PositionSizeDisplay';
import { Skeleton } from '@/components/Skeleton';
import TokenPairSimple from '@/components/TokenPair/TokenPairSimple';
import { Tooltip } from '@/components/ToolTip';
import LeanMoreToolTip from '@/components/ToolTip/LeanMoreToolTip';
import PnlTitleToolTip from '@/components/ToolTip/PnlTitleToolTip';
import { PAIR_PAGE_TYPE } from '@/constants/global';
import { FAQ_LINKS } from '@/constants/links';
import { TRADE_OVERLAY_TYPE, TRADE_TYPE } from '@/constants/trade';
import { resetLiquidityFormByChainId } from '@/features/earn/action';
import { useCurrentMarketType, useIsIpBlocked, useTheme } from '@/features/global/hooks';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { useFetchPortfolioListFromApi } from '@/features/portfolio/query';
import { useTradeType } from '@/features/trade/hooks';
import { useTradeStore } from '@/features/trade/store';
import { useAppDispatch } from '@/hooks';
import { useFilteredPositions } from '@/hooks/trade/useFilteredPositions';
import { usePriceBasisForPnl } from '@/hooks/usePriceBasisForPnl';
import { useSideNavigate } from '@/hooks/useRouterNavigate';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { TabKeys } from '@/pages/trade/TradePortfolio/TradePortfolioTabs';
import AdjustMarginLink from '@/pages/trade/TradePortfolio/TradePosition/AdjustMarginLink';
import SharePnlButton from '@/pages/trade/TradePortfolio/TradePosition/SharePnlButton';

interface IPropTypes {
  className?: string;
  isTradePage?: boolean;
}
const TradePositionMobile: FC<IPropTypes> = function ({ isTradePage = true }) {
  const { priceBasisForPnl } = usePriceBasisForPnl();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { dataTheme } = useTheme();
  const { t } = useTranslation();
  const tradeType = useTradeType(chainId);
  const dispatch = useAppDispatch();
  const pageTye = useCurrentMarketType();

  const { setShowTradeAreaOverlay } = useTradeStore();
  const { pairPageNavigate } = useSideNavigate();

  // Fetch and filter positions for current pair
  const currentPair = useCurrentPairFromUrl(chainId);
  const positionList = useFilteredPositions({
    currentPair,
    isTradePage,
  });

  const { isFetched: isFetchedPortfolio } = useFetchPortfolioListFromApi(chainId, userAddr);

  const ipBlocked = useIsIpBlocked();

  if (!isFetchedPortfolio) {
    return (
      <div className="syn-trade-position-mobile-skeleton">
        <div className="syn-trade-position-mobile-skeleton-top">
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
        </div>
        <div className="syn-trade-position-mobile-skeleton-row">
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
        </div>
        <div className="syn-trade-position-mobile-skeleton-row">
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
        </div>
      </div>
    );
  }
  return (
    <div
      className={classNames(
        'syn-trade-position-mobile',
        // !currentPosition || (currentPosition?.wrappedSize.eq(0) && 'syn-trade-position-mobile-no-position'),
        dataTheme,
      )}>
      {!positionList?.length && <Empty type="vertical" />}
      {positionList?.map((position) => {
        return (
          <div
            className="syn-mobile-profile-item"
            key={position.id}
            onClick={() => {
              pairPageNavigate(position.rootPair.symbol, PAIR_PAGE_TYPE.TRADE, undefined, {
                state: {
                  tab: TabKeys.POSITIONS,
                },
              });
            }}>
            <div className="syn-mobile-profile-item-header">
              <div className="syn-mobile-profile-item-title">
                <EmptyDataWrap isLoading={!position?.rootPair}>
                  <TokenPairSimple
                    tradeSide={position.wrappedSide}
                    leverage={position.leverage}
                    isInverse={position?.rootPair.isInverse}
                    baseToken={position?.rootPair?.rootInstrument.baseToken}
                    quoteToken={position?.rootPair?.rootInstrument.quoteToken}
                  />
                </EmptyDataWrap>
              </div>
              <div className="syn-mobile-profile-item-btns">
                <Tooltip showOnMobile={true} title={ipBlocked ? t('common.ipBlocker.tooltip') : undefined}>
                  <Button
                    ghost
                    disabled={ipBlocked}
                    onClick={(e) => {
                      e.stopPropagation();

                      const pairSymbol = position.rootPair.symbol;
                      pairPageNavigate(pairSymbol, PAIR_PAGE_TYPE.TRADE, chainId, { state: { toClose: true } });
                      if (tradeType !== TRADE_TYPE.CLOSE_POSITION) {
                        setShowTradeAreaOverlay(TRADE_OVERLAY_TYPE.CLOSE_POSITION);
                        if (pageTye === PAIR_PAGE_TYPE.EARN) {
                          chainId && dispatch(resetLiquidityFormByChainId({ chainId }));
                        }
                      }
                    }}
                    className="syn-trade-open-orders-cancel-btn">
                    {t('common.close')}
                  </Button>
                </Tooltip>
              </div>
            </div>
            <div className="syn-mobile-profile-item-body">
              <dl>
                <dt>{t('common.size')}</dt>
                <dd>
                  <PositionSizeDisplay position={position} />
                </dd>
              </dl>
              <dl>
                <dt>{t('common.tradePortfolio.positionV')}</dt>
                <dd>
                  <EmptyDataWrap isLoading={!position?.positionValue}>
                    {position.positionValue.abs().formatNumberWithTooltip({
                      suffix: position?.rootInstrument?.quoteToken?.symbol,
                      isShowTBMK: true,
                      showPositive: false,
                    })}
                  </EmptyDataWrap>
                </dd>
              </dl>
              <dl>
                <dt>{t('common.tradePortfolio.entryP')}</dt>
                <dd>
                  <EmptyDataWrap isLoading={!position?.entryPrice}>
                    {position?.wrapAttribute('entryPrice').formatPriceNumberWithTooltip()}
                  </EmptyDataWrap>
                </dd>
              </dl>
              <dl>
                <dt>{t('common.tradePortfolio.marketP')}</dt>
                <dd>
                  <EmptyDataWrap isLoading={!position?.rootPair?.markPrice}>
                    {position?.rootPair?.wrapAttribute('markPrice').formatPriceNumberWithTooltip()}
                  </EmptyDataWrap>
                </dd>
              </dl>
              <dl>
                <dt>
                  <PnlTitleToolTip />
                </dt>
                <dd>
                  {(() => {
                    const pnl = position?.getUnrealizedPositionPnlWithoutFunding(priceBasisForPnl);
                    const pnlPercentage = position?.getUnrealizedPnlPercentage(priceBasisForPnl);

                    return position?.unrealizedSocialLoss.lte(0) ? (
                      <div className="syn-trade-position-table-row">
                        {position?.getUnrealizedPositionPnlWithoutFunding(priceBasisForPnl).formatNumberWithTooltip({
                          suffix: position.rootInstrument.marginToken.symbol,
                          isShowTBMK: true,
                          colorShader: true,
                          showPositive: true,
                          colorSuffix: true,
                          isShowApproximatelyEqualTo: false,
                        })}
                        {position?.equity.gt(0) &&
                          position?.getUnrealizedPnlPercentage(priceBasisForPnl).formatPercentage({
                            decimals: 2,
                            suffix: ')',
                            prefix: '(',
                            isHiddenIfZero: true,
                          })}
                        <SharePnlButton position={position} />
                      </div>
                    ) : (
                      <EmptyDataWrap isLoading={!pnl}>
                        <div className="syn-trade-position-table-row">
                          <span className={pnl?.gt(0) ? 'text-green' : pnl?.lt(0) ? 'text-red' : ''}>
                            {pnl?.formatNumberWithTooltip({
                              isShowTBMK: true,
                              showPositive: true,
                              suffix: position?.rootInstrument?.marginToken?.symbol,
                            })}
                          </span>
                          {pnlPercentage && (
                            <span
                              className={pnlPercentage?.gt(0) ? 'text-green' : pnlPercentage?.lt(0) ? 'text-red' : ''}>
                              {pnlPercentage.formatPercentage({
                                decimals: 2,
                                requirePlusSymbol: true,
                                prefix: '(',
                                suffix: ')',
                              })}
                            </span>
                          )}
                          <SharePnlButton position={position} />
                        </div>
                      </EmptyDataWrap>
                    );
                  })()}
                </dd>
              </dl>
              <dl>
                <dt>{t('common.tradePortfolio.liqP')}</dt>
                <dd>
                  <EmptyDataWrap isLoading={!position?.liquidationPrice}>
                    {position?.liquidationPrice.formatPriceNumberWithTooltip()}
                  </EmptyDataWrap>
                </dd>
              </dl>
              <dl>
                <dt>{t('common.margin')}</dt>
                <dd>
                  <EmptyDataWrap isLoading={!position?.rootPair}>
                    <span className="syn-trade-position-table-row">
                      {position?.equity.formatNumberWithTooltip({
                        isShowTBMK: true,
                        suffix: position.rootPair.rootInstrument.marginToken.symbol,
                      })}{' '}
                      <AdjustMarginLink chainId={position.rootPair.chainId} pairSymbol={position.rootPair.symbol} />
                    </span>
                  </EmptyDataWrap>
                </dd>
              </dl>
              <dl>
                <dt>
                  <LeanMoreToolTip
                    link={FAQ_LINKS.PREDICTED_FUNDING_RATE}
                    overlayInnerStyle={{ width: 270 }}
                    title={
                      <Trans
                        i18nKey={'common.tradePage.tradePosition.unRealizedFundingTooltip'}
                        components={{ b: <b /> }}
                      />
                    }>
                    {t('common.tradePortfolio.funding')}
                  </LeanMoreToolTip>
                </dt>
                <dd>
                  <EmptyDataWrap isLoading={!position?.liquidationPrice}>
                    {position?.wrapAttribute('unrealizedFundingFee').formatNumberWithTooltip({
                      suffix: position.rootInstrument.marginToken.symbol,
                      isShowTBMK: true,
                      colorShader: true,
                      showPositive: true,
                      colorSuffix: true,
                      isShowApproximatelyEqualTo: false,
                    })}
                  </EmptyDataWrap>
                </dd>
              </dl>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TradePositionMobile;
